/**
 * Purchase-order list widgets (#1805 slice A3a).
 *
 * Six columns of the PO list carried `fields[].render`, which has no v2 carrier,
 * so each becomes a named widget here. Three things about them are load-bearing
 * and must not be "tidied":
 *
 *   - Each declares `csvText: rawCsv`. That is not a design choice, it is the
 *     CSV the legacy `render` carrier already produced: `formatFieldTextForExport`
 *     degrades a bespoke renderer to `stringifyRaw(value)` because arbitrary JSX
 *     has no text projection. Omitting it moves the column to its field TYPE's
 *     projection, which for `status` starts emitting a TRANSLATED enum label and
 *     for the three datetimes a formatted local timestamp. `csv-export-lock.test.ts`
 *     catches it, but nothing on screen would.
 *   - The markup is copied verbatim, class list included — the parity lock
 *     compares rendered HTML byte for byte on both surfaces.
 *   - `purchaseOrderRawOrDash` prints a BARE value, with no wrapping element.
 *     The legacy renderers were `<>{row.x ?? '—'}</>`, and wrapping them in a
 *     span to look like the others would change the DOM.
 *
 * The `id` column is deliberately NOT here. It carried `fields[].copyable`,
 * which usually has to become a widget (see `features/audit/widgets.tsx` and
 * `features/webhooks/widgets.tsx` for the trap: `copyable` RENDERS through the
 * id widget but EXPORTS through the field's own type, so re-typing a `text`
 * column to `id` keeps the markup byte-identical while moving its export from
 * `'' → -` to `'' → ''`). That trap does not reach this column, because it is
 * ALREADY `type: 'id'` — both projections were the id type's to begin with, so
 * dropping `copyable` moves nothing. See `purchaseOrderResource.tsx` for the
 * evidence and `__tests__/purchaseOrderResource.v2-parity.test.tsx` for the
 * assertions that check it instead of assuming it.
 *
 * Only TYPE imports from `@/services`, deliberately: `src/features/widgets.ts`
 * is evaluated by `src/test-setup.ts` for EVERY suite, ahead of any `vi.mock`,
 * so a service reached from a widget module at RUNTIME is instantiated against
 * the real `@lib/api` before a test can replace it. `import type` is erased, so
 * it carries the compile-time status set without that cost — the same
 * accommodation `features/stocktake/widgets.tsx` makes.
 */
import i18n from '@/i18n';
import { failHard } from '@resource-kit/core';
import { stringifyRaw } from '@resource-kit/ui/react';
import { registerWidget } from '@resource-kit/controllers';
import type { WidgetCsvContext, WidgetProps } from '@resource-kit/model';
import type { PurchaseOrder, PurchaseOrderStatus } from '@/services/procurement';

declare module '@resource-kit/core' {
  interface WidgetRegistry {
    purchaseOrderStatus: { types: 'enum'; modes: 'list' };
    purchaseOrderRawOrDash: { types: 'datetime' | 'text'; modes: 'list' };
    purchaseOrderTotal: { types: 'text'; modes: 'list' };
  }
}

const EM_DASH = '—';

/** The legacy `render` carrier's CSV projection, kept exactly (see the header). */
const rawCsv = ({ value }: WidgetCsvContext): string => stringifyRaw(value);

/**
 * The purchase order behind the cell.
 *
 * A row-aware projection mounted without its record is an authoring mistake, not
 * a value to render around: the previous `render` callbacks read `row.status` /
 * `row.currency` unguarded and would have thrown on the same input. Failing here
 * says which widget and why.
 */
function purchaseOrderOf(record: unknown, widget: string): PurchaseOrder {
  if (typeof record !== 'object' || record === null) {
    return failHard(`${widget}: mounted without a purchase-order record`);
  }
  return record as PurchaseOrder;
}

/**
 * Status → badge classes, colour-coding list and detail alike.
 *
 * The `default` arm is reachable in practice, not just for exhaustiveness: an
 * out-of-set wire status (a backend rename landing before a frontend deploy)
 * falls through to the neutral grey chip and still renders, rather than showing
 * an unstyled cell.
 */
function statusClass(status: PurchaseOrderStatus): string {
  switch (status) {
    case 'submitted':
      return 'bg-primary/10 text-primary';
    case 'partially_received':
      return 'bg-warning/10 text-foreground';
    case 'received':
    case 'closed':
      return 'bg-success/10 text-foreground';
    case 'cancelled':
      return 'bg-destructive/10 text-foreground';
    case 'draft':
    default:
      return 'bg-muted text-muted-foreground';
  }
}

/**
 * Reads the ROW, exactly as the `render` callback did. `i18n.t` is the singleton
 * the bespoke renderer used; an unknown status resolves to the raw key, which is
 * the pre-existing behaviour the parity golden freezes.
 */
function PurchaseOrderStatusCell({ record }: WidgetProps) {
  const row = purchaseOrderOf(record, 'purchaseOrderStatus');
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${statusClass(row.status)}`}>
      {i18n.t(`procurement.status.${row.status}`)}
    </span>
  );
}

/**
 * The nullable-column projection shared by `ordered_at`, `expected_delivery_at`,
 * `received_at` and `notes`: print the value, or an em dash when there is none.
 *
 * Reads `value` rather than the record because that is the ONE thing that
 * differs between the four columns, and none of them declares a `transform`, so
 * `value` is exactly the `row[key]` the legacy renderers read. Note the
 * distinction the dash keeps: `null` / `undefined` become the dash, while an
 * EMPTY STRING prints as empty — which is what `?? '—'` did.
 */
function PurchaseOrderRawOrDash({ value }: WidgetProps) {
  return <>{value === null || value === undefined ? EM_DASH : String(value)}</>;
}

/**
 * Amount plus currency, from the ROW — the only column that reads two members.
 * `total_amount ?? '0'` is the legacy fallback; the currency is printed raw, so
 * a PO with an empty currency renders a trailing space exactly as before.
 */
function PurchaseOrderTotal({ record }: WidgetProps) {
  const row = purchaseOrderOf(record, 'purchaseOrderTotal');
  return (
    <span className="tabular-nums">
      {row.total_amount ?? '0'} {row.currency}
    </span>
  );
}

/** Called by the feature barrel, which the composition root calls once. */
export function registerPurchaseOrderWidgets(): void {
  // No `input` here (ADR-0030): resolution refills it from the field type
  // default. `modes` does NOT gate that —
  // `packages/resource-kit-ui/react/form/FieldSlot.tsx::FieldSlot` resolves
  // unconditionally, readonly included. What is dead is the edit/create
  // surface:
  // `packages/resource-kit-ui/react/resource/ResourcePage.tsx::ResourcePageInner`
  // gates canCreate/canUpdate on
  // `api.create`/`api.update`, and this model exposes only `list`/`get`
  // (`src/features/procurement/purchaseOrderResource.tsx::buildPurchaseOrderResource`).
  // So the refill — not the deleted
  // borrow — is what gives `purchaseOrderRawOrDash`, which spans
  // `datetime | text`, the right control per type if PO ever gains an update.
  registerWidget('purchaseOrderStatus', {
    display: PurchaseOrderStatusCell,
    csvText: rawCsv,
    supportedTypes: ['enum'],
    modes: ['list'],
  });
  registerWidget('purchaseOrderRawOrDash', {
    display: PurchaseOrderRawOrDash,
    csvText: rawCsv,
    supportedTypes: ['datetime', 'text'],
    modes: ['list'],
  });
  registerWidget('purchaseOrderTotal', {
    display: PurchaseOrderTotal,
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  });
}
